import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PlugZap, ShieldCheck, RefreshCw, Unplug } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg, fmtDT, lim } from "../lib/api";
import { PageHeader, useLoad, StatusBadge, CopyCurl } from "../components/common";
import BatchInbox from "../components/BatchInbox";

function AdminTestPush({ me, connected }) {
  const [qty, setQty] = useState(5);
  const [busy, setBusy] = useState(false);
  const [batches, reload] = useLoad(async () => (await api.get("/batches")).data.filter((b) => b.rep_id === me.id));
  const get = async () => {
    setBusy(true);
    try { const r = (await api.post("/admin/test-batch", { quantity: Number(qty) })).data; toast.success(`${r.leads} leads added to your test batch`); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const release = async (b) => {
    if (!window.confirm(`Return the ${b.lead_count - (b.funded_count || 0)} unfunded test leads to the pool now? They'll also be removed from your Entrance campaign.`)) return;
    try { const r = (await api.post(`/admin/test-batch/${b.id}/release`)).data; toast.success(`${r.released} leads returned to the pool`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const active = (batches || []).filter((b) => b.admin_test && b.status === "active");
  return (
    <div className="panel p-5 space-y-3" data-testid="admin-test-push">
      <h2 className="font-bold">Test push (admin)</h2>
      <p className="text-sm text-zinc-600">Pull a few real leads from the pool into your own batch (held for you for 7 days, like a rep's), then push it into a new campaign in your Entrance.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="number" min={1} max={25} className="fld w-24" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="admin-test-qty" />
        <Button onClick={get} disabled={busy || !(qty >= 1 && qty <= 25)} data-testid="admin-test-get-btn">{busy ? "Getting…" : "Get test leads"}</Button>
        {!connected && <span className="text-xs text-amber-700">Connect your Entrance above before pushing.</span>}
      </div>
      {active.length > 0 && <div className="flex flex-wrap gap-2">{active.map((b) => <Button key={b.id} size="sm" variant="outline" onClick={() => release(b)} data-testid={`admin-test-release-${b.id}`}>Release test batch ({b.lead_count} leads)</Button>)}</div>}
      <div className="overflow-x-auto"><BatchInbox batches={batches} reload={reload} retract="admin" /></div>
    </div>
  );
}

const STEPS = [
  "Open your Entrance account and go to Workbooks → API.",
  "Create a new API key, then copy both parts: the App key (usually starts with \"EGKS\") and the Auth key.",
  "Paste both below and click Connect. We check them with Entrance straight away.",
  "If your keys can see more than one workspace, pick the one your leads should go to.",
];

export function EntranceConnectBanner() {
  const [c] = useLoad(async () => (await api.get("/my-entrance")).data);
  if (!c || c.connected) return null;
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-4 flex flex-wrap items-center gap-3" data-testid="entrance-connect-banner">
      <PlugZap className="w-4 h-4 text-amber-700" />
      <span className="text-sm">{c.status === "invalid" ? "Entrance rejected your saved keys. Reconnect to push leads." : "Connect your Entrance account to push your leads."}</span>
      <Link to="/my-entrance" className="text-sm font-semibold underline" data-testid="entrance-connect-link">Open My Entrance</Link>
    </div>
  );
}

function Status({ c, setC, account }) {
  const qs = account ? `?account=${account}` : "";
  const run = async (fn, ok) => { try { setC(await fn()); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); } };
  const check = () => run(async () => { const r = (await api.post("/my-entrance/check")).data; return account ? (await api.get(`/my-entrance${qs}`)).data : r; }, "Keys checked with Entrance");
  const pick = (id) => run(async () => (await api.post(`/my-entrance/workspace${qs}`, { workspace_id: id })).data, "Workspace saved");
  const disconnect = () => window.confirm("Disconnect this Entrance account? Pushing to it will be blocked until you reconnect.") &&
    run(async () => (await api.delete(`/my-entrance${qs}`)).data, "Disconnected");
  const tid = (s) => (account ? `${s}-${account}` : s);
  return (
    <div className="panel p-5 space-y-3" data-testid={tid("my-entrance-status")}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge s={c.connected ? "completed" : c.status === "invalid" ? "rejected" : "pending"} label={c.connected ? "connected" : c.status === "invalid" ? "keys rejected" : "choose a workspace"} testid={tid("my-entrance-badge")} />
        <span className="text-sm">App key ends <span className="mono">{c.app_key_hint}</span>{c.workspace_name && <> · workspace <b>{c.workspace_name}</b></>}</span>
      </div>
      {c.workspaces?.length > 1 && (
        <label className="text-xs space-y-1 block max-w-sm"><div>Workspace that receives your leads</div>
          <select className="fld w-full" value={c.workspace_id || ""} onChange={(e) => pick(e.target.value)} data-testid={tid("my-entrance-workspace-select")}>
            <option value="" disabled>Pick a workspace</option>{c.workspaces.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.id})</option>)}</select></label>)}
      <div className="text-xs text-zinc-600" data-testid="my-entrance-last-check">Last checked with Entrance: {c.last_check ? `${fmtDT(c.last_check.at)} — ${c.last_check.ok ? "OK" : `failed (HTTP ${c.last_check.http_status})`}` : "never"}</div>
      {c.webhook && <div className="text-xs text-zinc-600" data-testid="my-entrance-webhook">Opt-out (STOP) webhook in your Entrance: {c.webhook.ok ? "registered" : `not registered (${(c.webhook.error || []).join("; ") || `HTTP ${c.webhook.http_status}`})`}</div>}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={check} data-testid={tid("my-entrance-check-btn")}><RefreshCw className="w-3.5 h-3.5 mr-1" />Check now</Button>
        <Button variant="ghost" size="sm" onClick={disconnect} data-testid={tid("my-entrance-disconnect-btn")}><Unplug className="w-3.5 h-3.5 mr-1" />Disconnect</Button>
      </div>
    </div>
  );
}

function ConnectForm({ setC, again, account }) {
  const [f, setF] = useState({ app_key: "", auth_key: "" });
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  const tid = (s) => (account ? `${s}-${account}` : s);
  const connect = async () => {
    setBusy(true); setFail(null);
    const clean = { app_key: f.app_key.trim(), auth_key: f.auth_key.trim() };
    try { setC((await api.put(`/my-entrance${account ? `?account=${account}` : ""}`, clean)).data); setF({ app_key: "", auth_key: "" }); toast.success("Entrance connected"); } catch (e) { toast.error(errMsg(e)); setFail({ msg: errMsg(e), curl: e?.response?.data?.detail?.curl }); }
    setBusy(false);
  };
  return (
    <div className="panel p-5 space-y-3" data-testid={tid("my-entrance-form")}>
      <h2 className="font-bold">{again ? "Replace keys" : "Connect your Entrance"}</h2>
      <div className="flex flex-wrap gap-2">
        <input className="fld mono flex-1 min-w-[200px]" placeholder="App key" value={f.app_key} onChange={(e) => setF({ ...f, app_key: e.target.value })} autoComplete="off" data-testid={tid("my-entrance-app-key")} />
        <input className="fld mono flex-1 min-w-[200px]" type="password" placeholder="Auth key" value={f.auth_key} onChange={(e) => setF({ ...f, auth_key: e.target.value })} autoComplete="off" data-testid={tid("my-entrance-auth-key")} />
        <Button onClick={connect} disabled={busy || !f.app_key || !f.auth_key} data-testid={tid("my-entrance-connect-btn")}>{busy ? "Checking…" : "Connect"}</Button>
      </div>
      {fail && (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-xs space-y-2" data-testid={tid("my-entrance-connect-error")}>
          <div className="text-rose-700">{fail.msg}</div>
          {fail.curl && <><pre className="mono text-[11px] whitespace-pre-wrap break-all bg-white border rounded p-2" data-testid={tid("my-entrance-connect-curl")}>{fail.curl}</pre>
            <CopyCurl curl={fail.curl} testid={tid("my-entrance-copy-curl-btn")} /></>}
        </div>)}
      <p className="text-[11px] text-zinc-500 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Keys are stored encrypted, only used for your own pushes and never shown again.</p>
    </div>
  );
}

function AccountBlock({ a, onChange }) {
  const [c, setC] = useState(a);
  const set = (x) => { setC({ ...c, ...x }); onChange(); };
  const hasKeys = c.status && c.status !== "not_connected";
  const rename = async () => {
    const name = window.prompt("Name this Entrance account", c.name || "");
    if (!name?.trim()) return;
    try { await api.patch(`/my-entrance/accounts/${a.account_id}`, { name: name.trim() }); setC({ ...c, name: name.trim() }); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-3" data-testid={`my-entrance-account-${a.account_id}`}>
      <div className="flex items-center gap-3">
        <h2 className="font-display text-xl font-bold">{c.name}</h2>
        <Button size="sm" variant="ghost" onClick={rename} data-testid={`my-entrance-rename-${a.account_id}`}>Rename</Button>
        <span className="text-xs text-zinc-500">{a.lead.allowance == null ? "Limits not set yet — ask your admin" : `${lim(a.lead.allowance)} leads · ${lim(a.text.allowance)} texts a day`}</span>
      </div>
      {hasKeys && <Status c={c} setC={set} account={a.account_id === "main" ? null : a.account_id} />}
      <ConnectForm setC={set} again={hasKeys} account={a.account_id === "main" ? null : a.account_id} />
    </section>
  );
}

function MultiAccounts({ data, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    try { await api.post("/my-entrance/accounts", { name: name.trim() }); setName(""); toast.success("Account added — connect its keys below"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-8" data-testid="my-entrance-multi">
      {data.accounts.map((a) => <AccountBlock key={a.account_id} a={a} onChange={reload} />)}
      <div className="panel p-5 space-y-3" data-testid="my-entrance-add-account">
        <h2 className="font-bold">Add another Entrance account</h2>
        <p className="text-sm text-zinc-600">Give it a name you'll recognise (e.g. "Account B"). You pick the account each time you request leads, and that batch is pushed only into it.</p>
        <div className="flex flex-wrap gap-2">
          <input className="fld w-64" maxLength={60} placeholder="Account name" value={name} onChange={(e) => setName(e.target.value)} data-testid="my-entrance-new-account-name" />
          <Button onClick={add} disabled={!name.trim()} data-testid="my-entrance-add-account-btn">Add account</Button>
        </div>
      </div>
    </div>
  );
}

export default function MyEntrancePage({ me }) {
  const [c, , , setC] = useLoad(async () => (await api.get("/my-entrance")).data);
  const [multi, reloadMulti] = useLoad(async () => (await api.get("/my-entrance/accounts")).data);
  if (!c || !multi) return null;
  const hasKeys = c.status && c.status !== "not_connected";
  return (
    <div data-testid="my-entrance-page" className={`space-y-6 ${me?.role === "admin" ? "max-w-6xl" : "max-w-3xl"}`}>
      <PageHeader title="My Entrance" sub="Connect your own Entrance account so your leads go straight into it." />
      {multi.multi_allowed ? <MultiAccounts data={multi} reload={reloadMulti} /> : <>
        {hasKeys && <Status c={c} setC={setC} />}
        <ConnectForm setC={setC} again={hasKeys} /></>}
      {me?.role === "admin" && <AdminTestPush me={me} connected={c.connected} />}
      <div className="panel p-5 space-y-3" data-testid="my-entrance-instructions">
        <h2 className="font-bold">How to set it up</h2>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
        <h3 className="font-semibold text-sm pt-2">What happens when you push leads</h3>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-700">
          <li>Your keys are checked with Entrance each time you sign in and again right before every push.</li>
          <li>Every batch you push creates its own new unsent campaign in your Entrance, named with your name and the date (e.g. "John Smith 2026-10-28"; a second push that day becomes "John Smith 2026-10-28 (2)"). Nothing is texted automatically: you can type the message when you push, or open the campaign in Entrance to write or edit it and send it, within your daily max.</li>
          <li>When the 7 days are up, unfunded contacts are removed from that campaign automatically.</li>
          <li>STOP replies in your Entrance are picked up automatically and those numbers are never sent to anyone again.</li>
        </ul>
      </div>
    </div>
  );
}
